import { Transition } from "@uirouter/react";
import { MenuNode, MenuRoute } from "../../components/menu";
import { VerificationDTO as AMLVerificationDTO } from "../../store/verification/models/dto/aml";
import { VerificationDTO as KYCVerificationDTO } from "../../store/verification/models/dto/kyc";
import { VerificationService } from "../../store/verification/service";
import { LeafRole, Or } from "../../utils/and-or-tree";
import { ShellRouteNames } from "../shell/route-names";
import { AllBusinessAml } from "./business-aml/all";
import { NewBusinessAml } from "./business-aml/new";
import { SingleBusinessAml } from "./business-aml/single";
import { AllKyb } from "./business-kyb/all";
import { NewBusinessKyb } from "./business-kyb/new";
import { SingleKyb } from "./business-kyb/single";
import { AllIndividualAml } from "./individual-aml/all";
import { NewIndividualAml } from "./individual-aml/new";
import { SingleIndividualAml } from "./individual-aml/single";
import { AllKyc, AllKYCsComponent } from "./individual-kyc/all";
import { NewIndividualKyc } from "./individual-kyc/new";
import { SingleKyc } from "./individual-kyc/single";

const names = {
  newIndividualKyc: `${ShellRouteNames.shell}.new-individual-kyc`,
  allIndividualKyc: `${ShellRouteNames.shell}.all-individual-kyc`,
  singleIndividualKyc: `${ShellRouteNames.shell}.single-individual-kyc`,
  newBusinessKyb: `${ShellRouteNames.shell}.new-business-kyb`,
  allBusinessKyb: `${ShellRouteNames.shell}.all-business-kyb`,
  singleBusinessKyb: `${ShellRouteNames.shell}.single-business-kyb`,

  newIndividualAml: `${ShellRouteNames.shell}.new-individual-aml`,
  allIndividualAml: `${ShellRouteNames.shell}.all-individual-aml`,
  singleIndividualAml: `${ShellRouteNames.shell}.single-individual-aml`,
  newBusinessAml: `${ShellRouteNames.shell}.new-business-aml`,
  allBusinessAml: `${ShellRouteNames.shell}.all-business-aml`,
  singleBusinessAml: `${ShellRouteNames.shell}.single-business-aml`,
};

const states: MenuRoute[] = [
  {
    name: names.newIndividualKyc,
    component: NewIndividualKyc,
    url: "/kyc/new",
    data: {
      title: "perform-one-off-kyc",
      roles: new Or(new LeafRole("ROLE_ADMIN"), new LeafRole("ROLE_MERCHANT")),
    },
  },
  {
    name: names.allIndividualKyc,
    component: AllKYCsComponent,
    url: "/kyc?query&status",
    data: {
      title: "view-all-kyc-reports",
      roles: new LeafRole("ROLE_ADMIN"),
    },
    params: {
      query: {
        type: "string",
        isOptional: true,
        dynamic: true,
      },
      status: {
        type: "string",
        isOptional: true,
        dynamic: true,
      },
    },
    resolve: [
      {
        token: "query",
        policy: { when: "EAGER" },
        deps: [Transition],
        resolveFn: (t: Transition) => t.params().query,
      },
      {
        token: "status",
        policy: { when: "EAGER" },
        deps: [Transition],
        resolveFn: (t: Transition) => t.params().status,
      },
    ],
  },
  {
    name: names.allIndividualKyc,
    component: AllKyc,
    url: "/kyc",
    data: {
      title: "view-all-kyc-reports",
      roles: new LeafRole("ROLE_MERCHANT"),
    },
  },
  {
    name: names.newBusinessKyb,
    component: NewBusinessKyb,
    url: "/kyb/new",
    data: {
      title: "perform-b2b-register-check",
      roles: new Or(new LeafRole("ROLE_ADMIN"), new LeafRole("ROLE_MERCHANT")),
    },
  },
  {
    name: names.allBusinessKyb,
    component: AllKyb,
    url: "/kyb",
    data: {
      title: "view-all-kyb-reports",
      roles: new Or(new LeafRole("ROLE_ADMIN"), new LeafRole("ROLE_MERCHANT")),
    },
  },
  {
    name: names.singleIndividualKyc,
    component: SingleKyc,
    url: "/kyc/:fraudCheckId",
    data: {
      title: "report-detail",
      roles: new Or(new LeafRole("ROLE_ADMIN"), new LeafRole("ROLE_MERCHANT")),
    },
    params: {
      fraudCheckId: {
        type: "string",
      },
    },
    resolve: [
      {
        token: "fraudCheckId",
        policy: { when: "EAGER" },
        deps: [Transition],
        resolveFn: (trans: Transition) => trans.params().fraudCheckId,
      },
      {
        token: "item",
        deps: ["fraudCheckId"],
        resolveFn: (fraudCheckId: string) =>
          VerificationService.getSingleKyc(fraudCheckId).catch((err) => err),
      },
    ],
    redirectTo: (trans: Transition) => {
      // getAsync tells the resolve to load
      let resolvePromise: Promise<
        KYCVerificationDTO.Responses.GetSingleKyc.T | Error
      > = trans.injector().getAsync("item");
      return resolvePromise.then((
        err // TODO: maybe redirect to 404
      ) => (err instanceof Error ? names.allIndividualKyc : undefined));
    },
  },
  {
    name: names.singleBusinessKyb,
    component: SingleKyb,
    url: "/kyb/:fraudCheckId",
    data: {
      title: "report-detail",
      roles: new Or(new LeafRole("ROLE_ADMIN"), new LeafRole("ROLE_MERCHANT")),
    },
    params: {
      fraudCheckId: {
        type: "string",
      },
    },
    resolve: [
      {
        token: "fraudCheckId",
        policy: { when: "EAGER" },
        deps: [Transition],
        resolveFn: (trans: Transition) => trans.params().fraudCheckId,
      },
      {
        token: "item",
        deps: ["fraudCheckId"],
        resolveFn: (fraudCheckId: string) =>
          VerificationService.getSingleKyb(fraudCheckId).catch((err) => err),
      },
    ],
    redirectTo: (trans: Transition) => {
      // getAsync tells the resolve to load
      let resolvePromise: Promise<
        KYCVerificationDTO.Responses.GetSingleKyb.T | Error
      > = trans.injector().getAsync("item");
      return resolvePromise.then((
        err // TODO: maybe redirect to 404
      ) => (err instanceof Error ? names.allIndividualKyc : undefined));
    },
  },
  // ---- AML
  {
    name: names.newIndividualAml,
    component: NewIndividualAml,
    url: "/aml/individual/new",
    data: {
      title: "perform-one-off-aml",
      roles: new Or(new LeafRole("ROLE_ADMIN"), new LeafRole("ROLE_MERCHANT")),
    },
  },
  {
    name: names.allIndividualAml,
    component: AllIndividualAml,
    url: "/aml/individual",
    data: {
      title: "view-all-aml-reports",
      roles: new Or(new LeafRole("ROLE_ADMIN"), new LeafRole("ROLE_MERCHANT")),
    },
  },
  {
    name: names.newBusinessAml,
    component: NewBusinessAml,
    url: "/aml/business/new",
    data: {
      title: "perform-one-off-b2b-aml",
      roles: new Or(new LeafRole("ROLE_ADMIN"), new LeafRole("ROLE_MERCHANT")),
    },
  },
  {
    name: names.allBusinessAml,
    component: AllBusinessAml,
    url: "/aml/business",
    data: {
      title: "view-all-aml-b2b-reports",
      roles: new Or(new LeafRole("ROLE_ADMIN"), new LeafRole("ROLE_MERCHANT")),
    },
  },
  {
    name: names.singleIndividualAml,
    component: SingleIndividualAml,
    url: "/aml/individual/:fraudCheckId",
    data: {
      title: "aml-report-detail",
      roles: new Or(new LeafRole("ROLE_ADMIN"), new LeafRole("ROLE_MERCHANT")),
    },
    params: {
      fraudCheckId: {
        type: "string",
      },
    },
    resolve: [
      {
        token: "fraudCheckId",
        policy: { when: "EAGER" },
        deps: [Transition],
        resolveFn: (trans: Transition) => trans.params().fraudCheckId,
      },
      {
        token: "item",
        deps: ["fraudCheckId"],
        resolveFn: (fraudCheckId: string) =>
          VerificationService.getSingleIndividualAml(fraudCheckId).catch(
            (err) => err
          ),
      },
    ],
    redirectTo: (trans: Transition) => {
      // getAsync tells the resolve to load
      let resolvePromise: Promise<
        AMLVerificationDTO.Responses.GetSingleIndividualAml.T | Error
      > = trans.injector().getAsync("item");
      return resolvePromise.then((
        err // TODO: maybe redirect to 404
      ) => (err instanceof Error ? names.allIndividualAml : undefined));
    },
  },
  {
    name: names.singleBusinessAml,
    component: SingleBusinessAml,
    url: "/aml/business/:fraudCheckId",
    data: {
      title: "aml-report-detail",
      roles: new Or(new LeafRole("ROLE_ADMIN"), new LeafRole("ROLE_MERCHANT")),
    },
    params: {
      fraudCheckId: {
        type: "string",
      },
    },
    resolve: [
      {
        token: "fraudCheckId",
        policy: { when: "EAGER" },
        deps: [Transition],
        resolveFn: (trans: Transition) => trans.params().fraudCheckId,
      },
      {
        token: "item",
        deps: ["fraudCheckId"],
        resolveFn: (fraudCheckId: string) =>
          VerificationService.getSingleBusinessAml(fraudCheckId).catch(
            (err) => err
          ),
      },
    ],
    redirectTo: (trans: Transition) => {
      // getAsync tells the resolve to load
      let resolvePromise: Promise<
        AMLVerificationDTO.Responses.GetSingleBusinessAml.T | Error
      > = trans.injector().getAsync("item");
      return resolvePromise.then((
        err // TODO: maybe redirect to 404
      ) => (err instanceof Error ? names.allBusinessAml : undefined));
    },
  },
];

const kycSidebar: MenuNode.MenuNode = {
  icon: "kycMenu",
  title: "know-your-client",
  children: [
    {
      icon: "kycNew",
      route: states[0],
    },
    {
      icon: "amlIndividualNew",
      route: states[7],
    },
    {
      icon: "kycAll",
      route: states[2],
      roles: new LeafRole("ROLE_MERCHANT"),
    },
    {
      icon: "kycAll",
      route: states[1],
      roles: new LeafRole("ROLE_ADMIN"),
    },
    {
      icon: "amlIndividualAll",
      route: states[8],
    },
  ],
  roles: new Or(new LeafRole("ROLE_MERCHANT"), new LeafRole("ROLE_ADMIN")),
};

const kybSidebar: MenuNode.MenuNode = {
  icon: "kybMenu",
  title: "know-your-business",
  children: [
    {
      icon: "kybNew",
      route: states[3],
    },
    {
      icon: "amlBusinessNew",
      route: states[9],
    },
    {
      icon: "kybAll",
      route: states[4],
    },
    {
      icon: "amlBusinessAll",
      route: states[10],
    },
  ],
  roles: new Or(new LeafRole("ROLE_MERCHANT"), new LeafRole("ROLE_ADMIN")),
};

export const FraudRoutes = {
  names,
  states,
  kycSidebar: kycSidebar,
  kybSidebar: kybSidebar,
};
